import { useCallback, useEffect, useMemo, useState, type DragEvent, type ThHTMLAttributes } from 'react';
import { Icon } from './Icon';
import { Modal } from './ui';

/**
 * أعمدة الجدول: أيّها يظهر وبأيّ ترتيب.
 *
 * الجدول يعرف عن السجل أكثر ممّا يتّسع لعرض الشاشة. ومن يتابع التحصيل
 * يريد «القيمة» و«آخر تواصل»، ومن يوزّع العمل يريد «المسؤول» و«المصدر» —
 * وترتيبٌ واحد يخدم أحدهما ويُتعب الآخر.
 *
 * ## يُحفظ في المتصفح لا في الخادم
 *
 * تفضيلٌ بصري يخصّ الشاشة التي يجلس أمامها، كاختيار البطاقات وللسبب نفسه:
 * جدولٌ وترحيلٌ ومسارٌ لتفضيل لا يُقرأ إلا من متصفحه.
 *
 * ## والمحفوظ يُنقّى عند القراءة
 *
 * الأعمدة تتغيّر — يُضاف حقلٌ مخصَّص ويُحذف آخر — وما حُفظ أمس قد يذكر
 * عمودًا لم يعد موجودًا. فالقراءة تُسقط المجهول وتُلحق الجديد بالآخر، ولا
 * تردّ التفضيل كله لأن عمودًا واحدًا اختفى.
 */

export interface ColumnDef {
  key: string;
  label: string;
  /** لا يُخفى ولا يُنقل — كعمود الاسم الذي يُعرف به الصفّ. */
  fixed?: boolean;
  /** المجموعة في نافذة الاختيار. */
  group?: string;
}

export interface ColumnState {
  /** المفاتيح بترتيب العرض، والمخفيّ ليس فيها. */
  order: string[];
  hidden: string[];
}

interface Stored {
  order: string[];
  hidden: string[];
}

export function useColumnChoice(
  storageKey: string,
  columns: readonly ColumnDef[],
  /**
   * ما يُخفى قبل أن يختار المستخدم.
   *
   * الكتالوج يعرض كل ما يصلح عمودًا، وأكثره لا يعني أكثر الناس. وعرضُه
   * كلّه أوّل مرة يجعل الجدول يخرج عن الشاشة عرضًا قبل أن يُقرأ صفّ.
   */
  defaultHidden: readonly string[] = [],
): {
  /** الأعمدة المعروضة بترتيبها — هذا ما يُرسم. */
  visible: ColumnDef[];
  state: ColumnState;
  setState: (next: ColumnState) => void;
  reset: () => void;
  /** هل خرج المستخدم عن الافتراضي — لتعليم الزرّ. */
  touched: boolean;
} {
  const [stored, setStored] = useState<Stored | null>(() => read(storageKey));

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) setStored(read(storageKey));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const state = useMemo<ColumnState>(() => {
    const known = new Set(columns.map((c) => c.key));

    /*
     * الترتيب المحفوظ يُنقّى ثم يُكمَّل.
     *
     * ما لم يعد موجودًا يسقط، وما استُحدث يُلحق بالآخر — فحقلٌ مخصَّص
     * جديد يظهر بدل أن يختفي لأن التفضيل حُفظ قبل إضافته.
     */
    const kept = (stored?.order ?? []).filter((k) => known.has(k));
    const missing = columns.map((c) => c.key).filter((k) => !kept.includes(k));

    /*
     * الافتراضي يُستعمل حين لا تفضيل محفوظ فقط.
     *
     * ولو خُلط بالمحفوظ لعاد المخفيّ افتراضيًّا كلّما أظهره المستخدم —
     * فيبدو الاختيار وكأنه لا يُحفظ.
     */
    const source = stored ? stored.hidden : defaultHidden;
    const hidden = source.filter(
      (k) => known.has(k) && !columns.find((c) => c.key === k)?.fixed,
    );

    return { order: [...kept, ...missing], hidden };
  }, [stored, columns, defaultHidden]);

  const visible = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return state.order
      .filter((k) => !state.hidden.includes(k))
      .map((k) => byKey.get(k))
      .filter((c): c is ColumnDef => Boolean(c));
  }, [state, columns]);

  const setState = useCallback(
    (next: ColumnState) => {
      setStored(next);
      write(storageKey, next);
    },
    [storageKey],
  );

  const reset = useCallback(() => {
    setStored(null);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* التخزين قد يكون ممنوعًا — والإعادة تعمل في الجلسة على أي حال. */
    }
  }, [storageKey]);

  return { visible, state, setState, reset, touched: stored !== null };
}

/* ==========================================================================
   سحب الأعمدة من رأس الجدول
   ========================================================================== */

/**
 * يجعل رؤوس الجدول نفسها قابلةً للسحب يمينًا ويسارًا.
 *
 * نافذة «الأعمدة» تبقى للإخفاء والإظهار وللترتيب بلوحة المفاتيح، وهذا
 * الخطّاف يختصر الطريق لمن يرى الجدول أمامه: يمسك العمود من رأسه ويضعه
 * حيث يريد. الحالة واحدة — ما يُسحب هنا يظهر مرتَّبًا هناك.
 */
export function useColumnDrag(
  state: ColumnState,
  onChange: (next: ColumnState) => void,
): {
  /** خصائص تُفرد على `<th>` — تجعله مسحوبًا ومقصدًا للإفلات. */
  thProps: (col: ColumnDef, className?: string) => ThHTMLAttributes<HTMLTableCellElement>;
} {
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [over, setOver] = useState<{ key: string; after: boolean } | null>(null);

  const thProps = useCallback(
    (col: ColumnDef, className?: string): ThHTMLAttributes<HTMLTableCellElement> => {
      const movable = !col.fixed;
      const classes = [
        className,
        movable ? 'th-drag' : undefined,
        dragKey === col.key ? 'is-dragging' : undefined,
        over?.key === col.key && dragKey && dragKey !== col.key
          ? over.after
            ? 'th-drop-after'
            : 'th-drop-before'
          : undefined,
      ]
        .filter(Boolean)
        .join(' ');

      /*
       * «بعد» تعني لاحقًا في ترتيب الأعمدة لا جهةً على الشاشة: في صفحة
       * عربية اللاحق يقع يسارًا، فنصف الخلية الأيسر إفلاتٌ بعدها والأيمن
       * قبلها — ويُقاس من اتجاه المستند لا من افتراضٍ ثابت.
       */
      const sideOf = (e: DragEvent<HTMLTableCellElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        const firstHalf =
          getComputedStyle(e.currentTarget).direction === 'rtl'
            ? e.clientX > r.left + r.width / 2
            : e.clientX < r.left + r.width / 2;
        return !firstHalf;
      };

      return {
        className: classes || undefined,
        draggable: movable,
        onDragStart: movable
          ? (e) => {
              e.dataTransfer.setData('text/goyone-col', col.key);
              e.dataTransfer.effectAllowed = 'move';
              setDragKey(col.key);
            }
          : undefined,
        onDragEnd: () => {
          setDragKey(null);
          setOver(null);
        },
        onDragOver: (e) => {
          if (!dragKey || dragKey === col.key) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const after = sideOf(e);
          setOver((prev) =>
            prev?.key === col.key && prev.after === after ? prev : { key: col.key, after },
          );
        },
        onDragLeave: () => {
          setOver((prev) => (prev?.key === col.key ? null : prev));
        },
        onDrop: (e) => {
          e.preventDefault();
          const key = e.dataTransfer.getData('text/goyone-col') || dragKey;
          setDragKey(null);
          setOver(null);
          if (!key || key === col.key) return;
          const next = state.order.filter((k) => k !== key);
          const at = next.indexOf(col.key);
          if (at < 0) return;
          next.splice(sideOf(e) ? at + 1 : at, 0, key);
          onChange({ ...state, order: next });
        },
      };
    },
    [state, onChange, dragKey, over],
  );

  return { thProps };
}

/* ==========================================================================
   الزرّ والنافذة
   ========================================================================== */

export function ColumnPickerButton({
  columns,
  state,
  onChange,
  onReset,
  touched,
}: {
  columns: readonly ColumnDef[];
  state: ColumnState;
  onChange: (next: ColumnState) => void;
  onReset: () => void;
  touched: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        className={`btn btn-sm${touched ? ' btn-ghost' : ''}`}
        onClick={() => setOpen(true)}
        title="اختر الأعمدة ورتّبها"
      >
        <Icon name="layers" size={14} />
        الأعمدة
      </button>

      {open && (
        <ColumnModal
          columns={columns}
          state={state}
          onChange={onChange}
          onReset={onReset}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ColumnModal({
  columns,
  state,
  onChange,
  onReset,
  onClose,
}: {
  columns: readonly ColumnDef[];
  state: ColumnState;
  onChange: (next: ColumnState) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const [dragging, setDragging] = useState<string | null>(null);

  const rows = state.order.map((k) => byKey.get(k)).filter((c): c is ColumnDef => Boolean(c));

  const move = (key: string, to: number) => {
    const next = state.order.filter((k) => k !== key);
    next.splice(Math.max(0, Math.min(to, next.length)), 0, key);
    onChange({ ...state, order: next });
  };

  const toggle = (key: string) => {
    const def = byKey.get(key);
    if (def?.fixed) return;
    onChange({
      ...state,
      hidden: state.hidden.includes(key)
        ? state.hidden.filter((k) => k !== key)
        : [...state.hidden, key],
    });
  };

  const shown = rows.filter((c) => !state.hidden.includes(c.key)).length;

  return (
    <Modal title="أعمدة الجدول" onClose={onClose}>
      <p className="tiny mute mb-3">
        اسحب لترتيب الأعمدة، وأزل العلامة لإخفاء ما لا يعنيك. المعروض الآن {shown} من{' '}
        {columns.length}.
      </p>

      <ul className="col-picker">
        {rows.map((c, i) => {
          const isHidden = state.hidden.includes(c.key);

          return (
            <li
              key={c.key}
              className={`col-row${isHidden ? ' is-hidden' : ''}${dragging === c.key ? ' is-dragging' : ''}`}
              draggable={!c.fixed}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/goyone-col', c.key);
                e.dataTransfer.effectAllowed = 'move';
                setDragging(c.key);
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const key = e.dataTransfer.getData('text/goyone-col');
                setDragging(null);
                if (key && key !== c.key) move(key, i);
              }}
            >
              <span className="col-grip" aria-hidden="true">
                <Icon name="menu" size={14} />
              </span>

              <label className="col-label">
                <input
                  type="checkbox"
                  checked={!isHidden}
                  disabled={c.fixed}
                  onChange={() => toggle(c.key)}
                />
                <span className="small">{c.label}</span>
                {c.fixed && <span className="tiny mute">ثابت</span>}
                {c.group && <span className="tiny mute">{c.group}</span>}
              </label>

              {/*
                السحب لا يعمل بلوحة المفاتيح ولا باللمس.
                والسهمان هما الطريق نفسه لمن لا يستعمل الفأرة — لا بديلًا
                ناقصًا عنه.
              */}
              <span className="col-move">
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => move(c.key, i - 1)}
                  disabled={i === 0 || c.fixed}
                  aria-label={`نقل ${c.label} لأعلى`}
                  title="لأعلى"
                >
                  <Icon name="chevronLeft" size={13} />
                </button>
                <button
                  className="btn btn-sm btn-icon btn-ghost"
                  onClick={() => move(c.key, i + 1)}
                  disabled={i === rows.length - 1 || c.fixed}
                  aria-label={`نقل ${c.label} لأسفل`}
                  title="لأسفل"
                >
                  <Icon name="chevronRight" size={13} />
                </button>
              </span>
            </li>
          );
        })}
      </ul>

      <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary" onClick={onClose}>
          تمّ
        </button>
        <button className="btn" onClick={onReset}>
          <Icon name="refresh" size={14} />
          أعِد الافتراضي
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   التخزين
   ========================================================================== */

function read(key: string): Stored | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) return null;
    return {
      order: parsed.order.filter((k): k is string => typeof k === 'string'),
      hidden: parsed.hidden.filter((k): k is string => typeof k === 'string'),
    };
  } catch {
    /* تخزينٌ ممنوع أو قيمةٌ تالفة: الافتراضي أسلم من التوقّف. */
    return null;
  }
}

function write(key: string, value: Stored): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ممنوع في نافذة خاصة — والتفضيل يعمل في الجلسة على أي حال. */
  }
}
